"use client";

import { motion } from "framer-motion";
import { Activity, BarChart3, BookOpen, GraduationCap, TrendingUp, Users } from "lucide-react";

const performanceData = [
  { subject: "Physics", value: 72 },
  { subject: "Chemistry", value: 68 },
  { subject: "Mathematics", value: 80 },
  { subject: "Biology", value: 76 },
];

export default function AnalyticsPreview() {
  return (
    <div className="relative w-full overflow-hidden bg-gradient-to-br from-[#071126] via-[#0a1833] to-[#0b1d3d] p-4 text-white sm:p-6">
      <div className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full bg-brand-gold/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-20 h-56 w-56 rounded-full bg-blue-500/10 blur-3xl" />

      <div className="relative z-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-medium text-white/45">Illustrative institute dashboard</p>
            <h2 className="mt-1 text-xl font-extrabold tracking-tight text-brand-gold sm:text-2xl">
              Performance Analytics
            </h2>
          </div>
          <span className="rounded-full border border-brand-gold/25 bg-brand-gold/10 px-3 py-1.5 text-[10px] font-bold text-brand-gold">
            Sample data
          </span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <MetricCard icon={<Users className="h-4 w-4" />} title="Students" value="Example" />
          <MetricCard icon={<BookOpen className="h-4 w-4" />} title="Exams" value="Example" />
          <MetricCard icon={<GraduationCap className="h-4 w-4" />} title="Average score" value="Example" />
          <MetricCard icon={<TrendingUp className="h-4 w-4" />} title="Growth" value="Example" />
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <motion.div
            whileHover={{ y: -2 }}
            className="rounded-2xl border border-white/10 bg-white/[0.045] p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] font-medium text-white/45">Sample report</p>
                <h3 className="mt-1 text-sm font-bold">Subject Performance</h3>
              </div>
              <Activity className="h-4 w-4 text-brand-gold" />
            </div>

            <div className="mt-5 space-y-4">
              {performanceData.map((item) => (
                <div key={item.subject}>
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-[10px] text-white/60">{item.subject}</span>
                    <span className="text-[10px] font-bold text-brand-gold">{item.value}%</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${item.value}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      className="h-full rounded-full bg-gradient-to-r from-brand-gold/70 to-brand-gold"
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[10px] text-white/40">Illustrative values, not student results.</p>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="rounded-2xl border border-white/10 bg-white/[0.045] p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[9px] font-medium text-white/45">Reporting tools</p>
                <h3 className="mt-1 text-sm font-bold">Institute Overview</h3>
              </div>
              <BarChart3 className="h-4 w-4 text-brand-gold" />
            </div>
            <div className="mt-4 space-y-2">
              {[
                ["Exam participation", "View reports"],
                ["Student performance", "Track progress"],
                ["Results and rankings", "Review outcomes"],
                ["Question bank", "Manage questions"],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.06] bg-white/[0.035] px-3 py-3">
                  <span className="text-[10px] text-white/60">{label}</span>
                  <span className="shrink-0 text-[10px] font-bold text-brand-gold">{value}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[10px] leading-4 text-white/40">
              Available reports depend on the institute setup and data collected.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon, title, value }: { icon: React.ReactNode; title: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.045] p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[9px] font-medium text-white/50">{title}</p>
        <span className="text-brand-gold">{icon}</span>
      </div>
      <p className="mt-2 text-sm font-extrabold text-white sm:text-base">{value}</p>
    </div>
  );
}
